/**
 * The app's half of the contract with the desktop shell.
 *
 * MIRROR OF desktop/src/shell-contract.ts. The two packages have separate
 * toolchains and no shared module, so the values are duplicated here rather
 * than imported — shellContract.test.ts compares the two files and fails the
 * build if they ever disagree. Change one, change the other.
 *
 * There is no preload and no IPC by design (remote login pages must get no
 * privileged surface), so everything the two sides share is a bare convention:
 * a URL scheme, a console prefix, two measurements. The pages the shell can
 * open are deliberately not in here — those are plain routes, and a path needs
 * no agreement beyond the router having it.
 */

/**
 * Height of the title bar the app draws where the shell suppresses the OS one.
 * The shell sizes the login drag strip and the Windows caption overlay to it;
 * the app reserves exactly this much at the top of its own chrome.
 */
export const TITLE_BAR_HEIGHT = 52;

/**
 * Clears the macOS traffic lights, so the app's own window controls can sit
 * beside them: trafficLightPosition in the shell plus the cluster's own width.
 */
export const TRAFFIC_LIGHT_INSET = 92;

/**
 * Width the Windows caption buttons occupy, plus breathing room. The app pads
 * its toolbar this much on the right and the login bar keeps its content clear
 * of the same band — one value, or the two surfaces disagree by a few pixels.
 */
export const WINDOWS_CAPTION_WIDTH = 146;

/** What the window controls get where nothing of the OS floats over them. */
export const WINDOW_CONTROLS_MARGIN = 16;

/**
 * Custom property the shell sets, to WINDOW_CONTROLS_MARGIN, while the window
 * is fullscreen: macOS takes the traffic lights away there and this side cannot
 * see it happen. Unset the rest of the time, and in a browser tab, leaving the
 * `var()` fallback in desktopShell.ts.
 */
export const WINDOW_CONTROLS_INSET_VAR = "--window-controls-inset";

/** Asks the shell to pop the native app menu; carries `x`/`y` in window coordinates. */
export const APP_MENU_URL = "clank-shell://app-menu";

/**
 * Prefix of the console line the app emits on every theme change — the one
 * channel a sandboxed page has for telling the shell what colour to paint the
 * Windows caption strip.
 */
export const THEME_LINE_PREFIX = "clank:theme=";

/**
 * Event the shell dispatches on the app's own window instead of navigating it,
 * so the page the user is on survives. `detail` is the path the shell would
 * otherwise have loaded — no vocabulary beyond the routes both sides already
 * agree on — and the app raises whatever overlay that address stands for.
 *
 * The shell only ever dispatches this into its own origin, and only once that
 * document has finished loading; anywhere else it falls back to loading the
 * path for real, which raises the same overlay over a fresh page.
 */
export const SHELL_OVERLAY_EVENT = "clank-shell:overlay";
